/**
 * Takes the board off the screen: one A4 landscape page, or one PNG.
 *
 * A studio without a spare screen prints the board and tapes it to the rack, so the export is not a
 * nice-to-have — it is the same feature through a different medium. It captures the LIVE board node
 * rather than re-building the layout for print, which is the only way the two can't drift.
 *
 * Follows the capture→jsPDF path already used by `utils/bodyMeasurementsPdfExport.ts`.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/** A4 landscape, in mm. The board is 16:9, so it is centred with a margin above and below. */
const PAGE_WIDTH_MM = 297;
const PAGE_HEIGHT_MM = 210;

const captureBoard = (node: HTMLElement): Promise<HTMLCanvasElement> =>
    html2canvas(node, {
        // The board is captured at its own 1600x900, NOT at the on-screen scale — the visible node
        // is inside a CSS transform, and html2canvas would otherwise bake the shrink into the file.
        width: node.offsetWidth,
        height: node.offsetHeight,
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: null,
    });

/** Strips what a filesystem (and a trainee's phone) will not take, keeping Hebrew intact. */
export const boardFileName = (programName: string | null | undefined, dayTitle: string): string =>
    [programName, dayTitle]
        .filter(Boolean)
        .join(' - ')
        .replace(/[\\/:*?"<>|]/g, '')
        .trim() || 'workout';

export const exportBoardToPdf = async (
    node: HTMLElement,
    fileName: string
): Promise<void> => {
    const canvas = await captureBoard(node);
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    const imgHeight = (canvas.height * PAGE_WIDTH_MM) / canvas.width;
    const top = Math.max(0, (PAGE_HEIGHT_MM - imgHeight) / 2);

    // The board is dark; without this the page is a poster floating on white.
    pdf.setFillColor(10, 15, 28);
    pdf.rect(0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM, 'F');
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, top, PAGE_WIDTH_MM, imgHeight);
    pdf.save(`${fileName}.pdf`);
};

export const exportBoardToPng = async (node: HTMLElement, fileName: string): Promise<void> => {
    const canvas = await captureBoard(node);
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `${fileName}.png`;
    link.click();
};
